// Pure math + formatting helpers shared by the recipe scaler and any
// other widget that needs to scale ingredient amounts (smoothie builder
// could plug in here later — same shape).
//
// All functions are pure: same inputs always produce the same output,
// no DOM access, easy to unit-test in vitest.
//
// Smart-rounding rules tuned to NZ breakfast recipes:
//   * Count units (eggs, slices, rashers): nearest whole; ½ allowed; awkward
//     fractions become a range like "1-2 eggs"
//   * Spoons / cups: nearest standard fraction (¼ ⅓ ½ ⅔ ¾)
//   * Mass / volume in g/ml: round to nearest 5 above 50, nearest 10 above 200,
//     nearest whole below 50
//   * Anything we don't recognise: 1-decimal place fallback

const FRACTIONS = [
  [ 0, "" ],
  [ 1 / 8, "⅛" ],
  [ 1 / 4, "¼" ],
  [ 1 / 3, "⅓" ],
  [ 1 / 2, "½" ],
  [ 2 / 3, "⅔" ],
  [ 3 / 4, "¾" ],
  [ 7 / 8, "⅞" ],
  [ 1, "" ]
]

const COUNT_UNITS = new Set([
  "piece", "pieces",
  "rasher", "rashers",
  "slice", "slices",
  "clove", "cloves",
  "leaf", "leaves",
  "medium", "large", "small",
  "pinch", "pinches",
  "tin", "tins",
  "handful", "handfuls",
  "biscuit", "biscuits",
  "stick", "sticks",
  "bunch", "bunches",
  "sachet", "sachets"
])

const SPOON_OR_CUP_UNITS = new Set([
  "tsp", "teaspoon", "teaspoons",
  "tbsp", "tablespoon", "tablespoons",
  "cup", "cups"
])

const MASS_OR_VOLUME_UNITS = new Set([
  "g", "kg", "ml", "litre", "litres", "l"
])

// ---- public API ----

export function scaleAmount(amount, fromServings, toServings) {
  const a = parseFloat(amount)
  const from = parseFloat(fromServings)
  const to = parseFloat(toServings)
  if (isNaN(a)) return null
  if (!from || from <= 0 || !to || to <= 0) return a
  return a * (to / from)
}

export function formatScaledAmount(value, unit) {
  if (value == null || isNaN(value)) return ""
  const u = (unit || "").toLowerCase().trim()

  if (COUNT_UNITS.has(u)) return formatCount(value)
  if (SPOON_OR_CUP_UNITS.has(u)) return formatFractional(value)
  if (MASS_OR_VOLUME_UNITS.has(u)) return formatMassOrVolume(value, u)
  return formatFallback(value)
}

// Convenience: scale + format in one call.
export function scaleAndFormat(amount, unit, fromServings, toServings) {
  const scaled = scaleAmount(amount, fromServings, toServings)
  if (scaled == null) return amount == null ? "" : String(amount)
  return formatScaledAmount(scaled, unit)
}

// ---- internals ----

function formatCount(value) {
  if (value < 0.25) return "—"
  if (value < 0.75) return "½"

  const rounded = Math.round(value)
  // If the value is awkwardly between two whole numbers (e.g., 1.5 eggs),
  // present as a range so the cook doesn't try to crack 1.5 of anything.
  if (Math.abs(value - rounded) > 0.2 && value > 1) {
    const lo = Math.floor(value)
    const hi = Math.ceil(value)
    return `${lo}-${hi}`
  }
  return rounded.toString()
}

function formatFractional(value) {
  if (value < 0.05) return "a pinch"

  const whole = Math.floor(value)
  const fracPart = value - whole

  // Snap to the nearest known fraction.
  let best = FRACTIONS[0]
  let bestDiff = Math.abs(fracPart - best[0])
  for (const candidate of FRACTIONS) {
    const diff = Math.abs(fracPart - candidate[0])
    if (diff < bestDiff) {
      best = candidate
      bestDiff = diff
    }
  }

  // If fractional part is nearly a whole, bump up.
  if (best[0] === 1) {
    return (whole + 1).toString()
  }
  if (best[0] === 0) {
    return whole.toString()
  }
  if (whole === 0) return best[1]
  return `${whole}${best[1]}`
}

function formatMassOrVolume(value, unit) {
  // For kg/litre, scale to the underlying unit before rounding so we
  // don't end up with "1.4 kg" when "1400 g" would be tidier. Keep the
  // unit display itself unchanged.
  let v = value
  if (unit === "kg" || unit === "litre" || unit === "litres" || unit === "l") {
    if (v < 1) return v.toFixed(2).replace(/\.?0+$/, "")
    return v.toFixed(1).replace(/\.0$/, "")
  }
  if (v < 1) return v.toFixed(1).replace(/\.0$/, "")
  if (v < 50) return Math.round(v).toString()
  if (v < 200) return (Math.round(v / 5) * 5).toString()
  return (Math.round(v / 10) * 10).toString()
}

function formatFallback(value) {
  if (value < 1) return value.toFixed(1).replace(/\.0$/, "")
  if (Math.abs(value - Math.round(value)) < 0.05) return Math.round(value).toString()
  return value.toFixed(1)
}
